import type {
  AdTags,
  CaptchaSmsParams,
  InvitationCodeInfo,
  LoginParams,
  MemberInfo,
  RegisterParams,
  WeChatQrcode
} from '@/typings/account'
import ApiClient from '@/utils/api-client'
import { APP_LOGIN_REDIRECT_URI, DISCORD_REDIRECT_URI } from '@/constants/account'
import { getBaseURL, isElectron } from '@/utils/common'

export function login(
  data: LoginParams
): Promise<{
  token: string
  suid: string
  email: string
  redirect: { page: string }
  discord?: { avatar?: string; globalName?: string }
  profile?: { avatar?: string; nickname?: string }
  user?: { avatar?: string; nickname?: string }
}> {
  return ApiClient.post(`${getBaseURL()}/user/login`, data)
}

export function wechatCheck(
  code: string,
  key: string,
  token: string
): Promise<{ token: string; key: string }> {
  return ApiClient.get(`${getBaseURL()}/user/login/wechat/check`, { code, key, token })
}

export function getDiscordUrl(): Promise<{ url: string }> {
  return ApiClient.get(
    `${getBaseURL()}/user/login/discord?redirect_uri=${isElectron() ? APP_LOGIN_REDIRECT_URI : DISCORD_REDIRECT_URI}`
  )
}

export function getAccountInfo(action: string, token: string): Promise<{
  action: string
  token: string
  suid: string
}> {
  return ApiClient.post(`${getBaseURL()}/user/login/account-choice`, { action, token })
}
// export function getCaptcha(): Promise<{ key: string; captcha: string }> {
//   return ApiClient.post(`${baseURL}/user/captcha`, { type: 0 })
// }

export function captchaSms(data: CaptchaSmsParams): Promise<{ captcha: string; key: string }> {
  return ApiClient.post(`${getBaseURL()}/user/captchaSms`, data)
}

export function register(data: RegisterParams): Promise<{ token: string; userId: number }> {
  return ApiClient.post(`${getBaseURL()}/user/register`, data)
}

export function getBalance(userId: string): Promise<{ balance: number; lastId: number }> {
  return ApiClient.get(`${getBaseURL()}/credit/balance?userId=${userId}`)
}

export function logout() {
  // TODO:
}

export function getWeChatQrcode(): Promise<WeChatQrcode> {
  return ApiClient.post(`${getBaseURL()}/user/login/wechat`)
}

export function invite(code: string) {
  return ApiClient.post(`${getBaseURL()}/invite/invite/register`, { code })
}

export function setProfile(nickname: string, avatarResourceId: string) {
  return ApiClient.post(`${getBaseURL()}/user/profile`, { nickname, avatar: avatarResourceId })
}

export function getProfile(): Promise<{
  nickname: string
  avatar: string
  suid: string
  invited?: boolean
}> {
  return ApiClient.get(`${getBaseURL()}/user/profile`)
}

export function getCaptcha(
  countryCode: string,
  phone: string
): Promise<{ key: string; captcha: string }> {
  return ApiClient.post(`${getBaseURL()}/user/auth/captchaSms`, { countryCode, phone })
}

export function bindPhone(
  countryCode: string,
  phone: string,
  captchaKey: string,
  captchaValue: string
) {
  return ApiClient.post(`${getBaseURL()}/user/auth/bindPhone`, {
    countryCode,
    phone,
    captcha: { key: captchaKey, value: captchaValue }
  })
}

export function getBindPhoneInfo(): Promise<{ wechat: string; phone: string }> {
  return ApiClient.get(`${getBaseURL()}/user/auth`)
}

export function getMemberInfo(): Promise<MemberInfo> {
  return ApiClient.get(`${getBaseURL()}/member/user-member`)
}

export function getInvitationCode(): Promise<InvitationCodeInfo> {
  return ApiClient.get(`${getBaseURL()}/invite/invite`)
}

export function requireInvitationCode(): Promise<{ status: number }> {
  return ApiClient.get(`${getBaseURL()}/invite/require`)
}

export function getPromotions(): Promise<{ key: string; id: number; platform: string }[]> {
  return ApiClient.get(`${getBaseURL()}/user/promotions?platform=common&platform=pc&platform=web`)
}

export function setPromotion(key: string, status = 1) {
  return ApiClient.post(`${getBaseURL()}/user/promotions`, { key, status })
}

export function feedback(reason: string, description: string) {
  return ApiClient.post(`${getBaseURL()}/user/feedback`, { reason, description })
}

export function addAdTags(tags: AdTags) {
  return ApiClient.post(`${getBaseURL()}/user/ad/add-tag`, tags)
}
